<template>
  <div>
    <p class="dialog-input-label my-0"> {{ label }} </p>
    <v-tooltip
      v-model="error"
      left
      >
      <template v-slot:activator="{ attrs }">
        <v-textarea
          v-if="textarea"
          :placeholder="placeholder"
          :value="value"
          :rules="rules ? mappedRules : false"
          :class="[error ? 'validation-error': '', autoGrow ? 'autogrow':'']"
          :readonly="readonly ? true: false"
          :height="height ? height: 'auto'"
          dense
          solo
          flat
          full-width
          no-resize
          :auto-grow="autoGrow"
          ref="textField"
          @input="$emit('input', { name, value: useNumbers ? parseInt($event, 10) : $event }); textareaExpandable && expandTextarea()"
          v-on:keyup="validateMethod($event.target.value, validationType)"
        >
          <v-icon v-if="prependIcon" slot="prepend-inner" :color="iconColor">
            {{ prependIcon }}
          </v-icon>
        </v-textarea>
        <v-text-field
          v-else
          :placeholder="placeholder"
          :value="value"
          :rules="rules ? mappedRules : false"
          :error-messages="errorMessages"
          :type="useNumbers ? 'number' : 'text'"
          :class="error ? 'validation-error': ''"
          :readonly="readonly ? true: false"
          height="36"
          dense
          solo
          flat
          full-width
          @input="$emit('input', { name, value: useNumbers ? parseInt($event, 10) : $event })"
          v-on:keyup="validateMethod($event.target.value, validationType)"
        >
          <v-icon v-if="prependIcon" slot="prepend-inner" :color="iconColor">
            {{ prependIcon }}
          </v-icon>
        </v-text-field>
        <span class=""
          v-bind="attrs"
        ></span>
      </template>
      <span>This field value is invalid</span>
    </v-tooltip>
  </div>
</template>

<script>
import { debounce } from '@/helpers/debounce'
import { isValidUsPhone } from '@/helpers/phoneFormat'
export default {
  name: 'DialogInput',
  data: () => ({
    error: false,
    rulesArray: {
      counter: value => value.length <= 250 || 'Max 250 characters',
    }
  }),
  props: {
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      required: true
    },
    value: {
      type: null,
      default: null
    },
    useNumbers: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default: () => []
    },
    prependIcon: {
      type: String,
      default: undefined
    },
    iconColor: {
      type: String,
      default: 'primary'
    },
    validate: {
      type: Boolean,
      default: false
    },
    // Inline message rendered under the field, owned by the parent form. Unlike
    // the tooltip above this is always visible, which is what the 10DLC opt-in
    // screenshot needs for the phone field.
    errorMessages: {
      type: [String, Array],
      default: () => []
    },
    validationType: {
      type: String,
      default: 'required'
    },
    readonly: {
      type: Boolean,
      default: false
    },
    textarea: {
      type: Boolean,
      default: false
    },
    textareaExpandable: {
      type: Boolean,
      default: false
    },
    height: {
      type: [Boolean, Number],
      default: false
    },
    counter: {
      type: Boolean,
      default: false
    },
    autoGrow: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    mappedRules() {
      return this.rules?.map(rule=>{
        if (this.rulesArray[rule]) return this.rulesArray[rule]
      })

    }
  },
  watch: {
    value (val) {
      if (this.textareaExpandable) {
        if (val) {
          this.expandTextarea()
        } else {
          this.expandTextarea(true)
        }
      }
    }
  },
  mounted() {
    if (this.textareaExpandable) this.expandTextarea(this.value ? false : true)
  },
  methods: {
    validateMethod(value) {
      if (this.validate) {
        this.error = false
        this.newValue = value
        if (this.validationType == 'email' && value)
          debounce(() => this.validEmail(value), 1000, 'DialogInput')
        if (this.validationType == 'digits' && value)
          debounce(() => this.validDigits(value), 1000, 'DialogInput')
        if (this.validationType == 'usPhone' && value)
          debounce(() => this.validUsPhone(value), 1000, 'DialogInput')
        if (this.validationType == 'length' && value) 
          debounce(() => this.validLength(value), 1000, 'DialogInput')
      }
    },
    validEmail: function (email) {
      let re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      let valid = re.test(email)
      this.error = !valid
      return valid
    },
    validDigits: function (number) {
      let re = /^\d+$/;
      let valid = re.test(number)
      this.error = !valid
      return valid
    },
    validUsPhone: function (number) {
      let valid = isValidUsPhone(number)
      this.error = !valid
      return valid
    },
    validLength: function (text) {
      let valid = text.length <= 250
      this.error = !valid
      return valid
    },
    expandTextarea(reset = false) {
      this.$nextTick(() => {
        const textField = this.$refs.textField.$refs.input
        if (reset) {
          textField.style.height = "20px"
        } else {
          textField.style.height = "5px"
          textField.style.height = (textField.scrollHeight)+"px"
        }
      })
    },
  }
}
</script>

<style lang="scss" >
@import "@/styles/variables.scss";

.dialog-input {
  &-label {
    color: $bh-gray--2;
  }
}
.v-input--is-readonly {
  .v-input__slot {
    background-color:$bh-blue--4 !important;
  }
}
.v-text-field__details {
  min-height: 0;
  .v-messages {
    min-height: 0;
  }
}
.autogrow {
  textarea {
    max-height: none;
  }
}
textarea {
  max-height:6*1.75rem;
  height: 20px;
}
.v-input.validation-error .v-input__slot {
  background-color:$bh-red !important;
  border-color:$bh-red !important;
}
</style>